
import { Shell } from "@/components/layout/Shell";
import { ContractsTable } from "@/components/contracts/ContractsTable";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useTableColumns } from "@/hooks/useTableColumns";
import { ColumnOption } from "@/components/contracts/ColumnSelector";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { useUserRoles } from "@/hooks/useUserRoles";
import { currencyConverter } from "@/services/currencyConverter";
import { useCurrencies } from "@/hooks/useCurrencies";
import { toast } from "@/hooks/use-toast";
import { ViewSwitcher, ViewType } from "@/components/contracts/ViewSwitcher";
import { getImportanceBadges } from "@/components/contracts/table/importanceBadges";
import { TranslationText } from "@/components/i18n/TranslationText";
import { useEnhancedTranslation } from "@/hooks/useEnhancedTranslation";
import { useTranslationEdit } from "@/contexts/TranslationEditContext";
import { TranslationButton } from "@/components/i18n/TranslationButton";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { FeatureGuard } from "@/components/common/FeatureGuard";
import { PlanSelectDialogProvider } from "@/contexts/PlanSelectDialogContext";
import { PlanSelectDialog } from "@/components/settings/PlanSelectDialog";
import { EmptyContractsState } from "@/components/contracts/EmptyContractsState";
import { useContracts } from "@/hooks/useContracts";

const DEFAULT_COLUMNS = [
  "checkbox", "supplierName", "importance", "expiryStatus", "type", "category", "expiryDate", "currency", "expectedCost", "actions"
];

export default function ContractsPage() {
  const { t, i18n, tElement } = useEnhancedTranslation();
  const isMobile = useIsMobile();
  const [refreshKey, setRefreshKey] = useState(0);
  const { useHasRoleQuery } = useUserRoles();
  const { useCurrenciesQuery } = useCurrencies();
  const { useContractsQuery } = useContracts();
  const [isUpdatingRates, setIsUpdatingRates] = useState(false);
  const [viewType, setViewType] = useState<ViewType>(() => {
    const savedView = localStorage.getItem('contractsViewType');
    return (savedView as ViewType) || 'table';
  });
  const { isEditMode } = useTranslationEdit();
  const { 
    canUseCurrencyConversion, 
    canUseMultipleCurrencies,
    canUsePriceComparison,
    canUseGroupedContracts
  } = useFeatureAccess();
  
  const { data: isAdmin } = useHasRoleQuery("ADMIN");
  
  const { data: currencies = [] } = useCurrenciesQuery();
  
  const { selectedColumns, updateSelectedColumns } = useTableColumns({
    tableId: "contracts",
    defaultColumns: DEFAULT_COLUMNS.filter(col => 
      col !== 'currency' || canUseMultipleCurrencies
    ),
  });
  
  const importanceBadges = getImportanceBadges();
  
  useEffect(() => {
    if (i18n.reloadResourcesFromDb) {
      i18n.reloadResourcesFromDb().then(() => {
        setRefreshKey(prevKey => prevKey + 1);
      });
    }
    
    const handleLanguageChanged = () => {
      console.log("Language changed, refreshing UI");
      setRefreshKey(prevKey => prevKey + 1);
    };
    
    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);
  
  useEffect(() => {
    localStorage.setItem('contractsViewType', viewType);
  }, [viewType]);
  
  const handleUpdateAllRates = async () => {
    if (!currencies.length) {
      toast.error(t("noCurrenciesAvailable"));
      return;
    }
    
    setIsUpdatingRates(true);
    try {
      for (const baseCurrency of currencies) {
        await currencyConverter.fetchAndStoreExchangeRates(baseCurrency, currencies);
      }
      toast.success(t("exchangeRatesUpdated"));
    } catch (error) {
      console.error("Error updating exchange rates:", error);
      toast.error(t("failedToUpdateRates"));
    } finally {
      setIsUpdatingRates(false);
    }
  };
  
  const handleViewChange = (newViewType: ViewType) => {
    if (!isEditMode) {
      setViewType(newViewType);
    }
  };

  const filterColumnsBasedOnFeatures = (columns: ColumnOption[]): ColumnOption[] => {
    return columns.filter(col => {
      if (col.id === 'currency' && !canUseMultipleCurrencies) {
        return false;
      }
      return true;
    });
  };

  const ALL_CONTRACT_COLUMNS: ColumnOption[] = filterColumnsBasedOnFeatures([
    { id: "checkbox", label: t("select"), required: true },
    { id: "supplierName", label: t("supplier"), required: true },
    { id: "importance", label: t("importance") },
    { id: "expiryStatus", label: t("status") },
    { id: "type", label: t("type") },
    { id: "category", label: t("category") },
    { id: "expiryDate", label: t("expiryDate") },
    { id: "signedDate", label: t("signedDate") },
    { id: "expectedCost", label: t("value") },
    { id: "currency", label: t("currency") },
    { id: "actions", label: t("actions"), required: true }
  ]);
  
  const { data: contracts = [], isLoading } = useContractsQuery();
  
  const hasContracts = contracts.length > 0;

  return (
    <PlanSelectDialogProvider>
      <Shell className="w-full max-w-full p-0">
        <div className="h-full w-full">
          {hasContracts && (
            <div className="px-4 py-3">
              <h1 className="text-3xl font-bold tracking-tight dark:text-white">
                <TranslationText i18nKey="contracts">
                  {t('contracts')}
                </TranslationText>
              </h1>
              <p className="text-muted-foreground mt-1 dark:text-slate-400">
                <TranslationText i18nKey="manageYourContracts">
                  {t('manageYourContracts')}
                </TranslationText>
              </p>
            </div>
          )}
          
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <p>Loading...</p>
            </div>
          ) : !hasContracts ? (
            <EmptyContractsState />
          ) : (
            <ContractsTable 
              key={refreshKey} 
              availableColumns={ALL_CONTRACT_COLUMNS.map(col => ({
                ...col,
                label: col.label
              }))}
              selectedColumns={selectedColumns.filter(col => 
                col === 'currency' ? canUseMultipleCurrencies : true
              )}
              onColumnChange={updateSelectedColumns}
              viewType={viewType}
              onViewChange={handleViewChange}
              isAdmin={!!isAdmin}
              onUpdateAllRates={canUseCurrencyConversion ? handleUpdateAllRates : undefined}
              isUpdatingRates={isUpdatingRates}
              importanceBadges={importanceBadges}
              canUseGroupedContracts={canUseGroupedContracts}
            />
          )}
        </div>
        <PlanSelectDialog />
      </Shell>
    </PlanSelectDialogProvider>
  );
}
